import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet-async';
import { subscribeToDAO } from '../utils/emailService';

const DAOPage = () => {
  const [notificationStatus, setNotificationStatus] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: ''
  });

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleGetNotified = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setNotificationStatus('');

    try {
      await subscribeToDAO(formData.name, formData.email);
      setNotificationStatus('✅ Success! You\'ll be notified when the DAO launches.');
      setFormData({ name: '', email: '' });
      setTimeout(() => setNotificationStatus(''), 5000);
    } catch (error) {
      console.error('Error submitting notification:', error);
      setNotificationStatus('❌ Error. Please try again.');
      setTimeout(() => setNotificationStatus(''), 5000);
    } finally {
      setIsSubmitting(false);
    }
  };
  return (
    <>
      <Helmet>
        <title>Petitioner DAO - Petition.io | Coming Soon</title>
        <meta name="description" content="The Petitioner DAO is coming soon. Join us in building the future of decentralized governance and revenue sharing." />
        <meta property="og:title" content="Petitioner DAO - Coming Soon" />
        <link rel="canonical" href="https://petition.io/dao" />
      </Helmet>

      <div className="min-h-screen bg-white pt-20">
        {/* Hero Section */}
        <section className="py-12 sm:py-16 md:py-20 bg-gray-50">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              className="text-center"
            >
              {/* Badge */}
              <motion.div
                initial={{ scale: 0.8, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                transition={{ duration: 0.6, delay: 0.2 }}
                className="inline-block mb-6 sm:mb-8"
              >
                <span className="px-4 sm:px-6 py-2 sm:py-3 bg-primary-blue/10 border border-primary-blue/20 rounded-full text-primary-blue text-xs sm:text-sm font-bold tracking-wider">
                  PETITIONER DAO
                </span>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.8, delay: 0.5 }}
                className="mb-8 sm:mb-10 mx-auto max-w-md md:max-w-lg"
              >
                <img 
                  src="/images/petitioner-DAO.jpg" 
                  alt="Petitioner DAO Coming Soon"
                  className="w-full h-auto mx-auto rounded-xl sm:rounded-2xl shadow-2xl object-contain"
                />
              </motion.div>

              {/* Coming Soon Heading */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.3 }}
              >
                <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold text-neutral-dark mb-4 sm:mb-6 px-4 font-heading">
                  Coming Soon
                </h1>

                <p className="text-lg sm:text-xl md:text-2xl text-gray-700 font-medium mb-4 sm:mb-6 max-w-3xl mx-auto leading-relaxed px-4">
                  The future of decentralized governance and volunteer-powered rewards is on its way.
                </p>
              </motion.div>


              {/* Coming Soon Text */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.7 }}
              >
                <p className="text-base sm:text-lg md:text-xl text-gray-600 mb-8 sm:mb-10 max-w-2xl mx-auto leading-relaxed px-4">
                  Join the Petitioner DAO to support global causes, help guide platform decisions, and receive decentralized crypto-based volunteer appreciation gifts — all fully aligned with nonprofit guidelines.
                </p>

                {/* Feature Pills */}
                <div className="flex flex-wrap justify-center gap-3 sm:gap-4 mb-8 sm:mb-10 px-4">
                  {['Support Causes', 'Guide Decisions', 'Volunteer Recognition'].map((feature, idx) => (
                    <motion.div
                      key={idx}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.6, delay: 0.9 + idx * 0.1 }}
                      whileHover={{ scale: 1.05, y: -3 }}
                      className="px-4 sm:px-6 py-2 sm:py-3 bg-primary-blue/5 border border-primary-blue/20 rounded-full text-primary-blue text-sm sm:text-base font-semibold shadow-sm hover:shadow-md transition-shadow"
                    >
                      {feature}
                    </motion.div>
                  ))}
                </div>

                {/* Subscription Form */}
                <motion.form
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: 1.2 }}
                  onSubmit={handleGetNotified}
                  className="max-w-md mx-auto px-4"
                >
                  <div className="bg-white rounded-2xl shadow-2xl p-6 sm:p-8 border border-gray-100">
                    <div className="text-center mb-6">
                      <div className="inline-flex items-center justify-center w-12 h-12 bg-primary-blue/10 rounded-full mb-3">
                        <svg className="w-6 h-6 text-primary-blue" fill="currentColor" viewBox="0 0 20 20">
                          <path d="M2.003 5.884L10 9.882l7.997-3.998A2 2 0 0016 4H4a2 2 0 00-1.997 1.884z" />
                          <path d="M18 8.118l-8 4-8-4V14a2 2 0 002 2h12a2 2 0 002-2V8.118z" />
                        </svg>
                      </div>
                      <h3 className="text-lg sm:text-xl font-bold text-neutral-dark mb-2">Get Notified at Launch</h3>
                      <p className="text-sm text-gray-600">Be the first to know when we go live</p>
                    </div>
                    
                    <div className="space-y-4">
                      <div>
                        <input
                          type="text"
                          name="name"
                          value={formData.name}
                          onChange={handleInputChange}
                          placeholder="Your Name"
                          required
                          className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:border-primary-blue focus:ring-2 focus:ring-primary-blue/20 transition-all outline-none text-sm sm:text-base"
                        />
                      </div>
                      <div>
                        <input
                          type="email"
                          name="email"
                          value={formData.email}
                          onChange={handleInputChange}
                          placeholder="your.email@example.com"
                          required
                          className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:border-primary-blue focus:ring-2 focus:ring-primary-blue/20 transition-all outline-none text-sm sm:text-base"
                        />
                      </div>
                      <motion.button
                        whileHover={{ scale: 1.02 }}
                        whileTap={{ scale: 0.98 }}
                        type="submit"
                        disabled={isSubmitting}
                        className="w-full px-6 py-3 sm:py-4 bg-gradient-to-r from-primary-blue to-primary-light text-white text-base sm:text-lg font-bold rounded-xl shadow-lg hover:shadow-xl transition-all disabled:opacity-60 disabled:cursor-not-allowed inline-flex items-center justify-center"
                      >
                        {isSubmitting ? (
                          <>
                            <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                            </svg>
                            Subscribing...
                          </>
                        ) : (
                          <>
                            <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-11a1 1 0 10-2 0v2H7a1 1 0 100 2h2v2a1 1 0 102 0v-2h2a1 1 0 100-2h-2V7z" clipRule="evenodd" />
                            </svg>
                            Notify Me
                          </>
                        )}
                      </motion.button>
                    </div>
                  </div>
                  
                  {/* Status Message */}
                  {notificationStatus && (
                    <motion.div
                      initial={{ opacity: 0, y: -10 }}
                      animate={{ opacity: 1, y: 0 }}
                      className="mt-4 text-center text-base sm:text-lg font-semibold px-4 py-3 bg-white rounded-xl shadow-lg"
                    >
                      {notificationStatus}
                    </motion.div>
                  )}
                </motion.form>
              </motion.div>
            </motion.div>
          </div>
        </section>
      </div>
    </>
  );
};

export default DAOPage;
